import moment from 'moment';

export type TimeSpanFilterValue = {
  mode: 'utc' | 'lmt' | 'recent';
  start?: string;
  end?: string;
  recent?: { value?: number; unit?: moment.unitOfTime.Base };
};

type Filter<T = any, Type extends string = string, Value = any, Option = undefined> = {
  type: Type;
  title: string;
  defaultValue?: Value;
  onChange?: (value: Value | undefined) => void;
  filter?: (data: T, value: Value | undefined) => boolean;
  options?: Option;
  sortOptions?: boolean;
  disabled?: boolean;
  hideToggle?: boolean;
  placeholder?: string;
  tooltip?: string;
};

export type MultiSelectFilter<T, U = any> = Filter<
  T,
  'multi-select',
  U[],
  { label: string; item: U }[]
>;
export type SingleSelectFilter<T, U = any> = Filter<
  T,
  'single-select',
  U,
  { label: string; item: U }[]
>;
export type InputFilter<T, U = any> = Filter<T, 'input', U, (input: string) => U>;
export type ProcessIdFilter<T> = Filter<T, 'process-id', string[]>;
export type SingleDateFilter<T> = Filter<T, 'single-date', string, { min?: string; max?: string }>;
export type DateFilter<T> = Filter<T, 'date', { beforeDate?: string; afterDate?: string }>;
export type TimeSpanFilter<T> = Filter<
  T,
  'time',
  TimeSpanFilterValue,
  { modes?: TimeSpanFilterValue['mode'][] }
>;
export type IntegerFilter<T> = Filter<
  T,
  'integer',
  number,
  {
    prefix?: string;
    suffix?: string;
    min?: number;
    max?: number;
    units?: { label: string; factor: number }[];
  }
>;
export type BooleanFilter<T> = Filter<T, 'boolean', boolean>;
export type MinMaxFilter<T> = Filter<
  T,
  'minMax',
  Record<string, { min?: number; max?: number }>,
  Record<string, { label?: string; min?: number; max?: number }>
>;

export type DatatableFilter<T> =
  | MultiSelectFilter<T>
  | SingleSelectFilter<T>
  | InputFilter<T>
  | ProcessIdFilter<T>
  | TimeSpanFilter<T>
  | IntegerFilter<T>
  | BooleanFilter<T>
  | MinMaxFilter<T>
  | DateFilter<T>
  | SingleDateFilter<T>;

export const typedFilter = <T, U>(
  filter:
    | MultiSelectFilter<T, U>
    | SingleSelectFilter<T, U>
    | InputFilter<T, U>
    | ProcessIdFilter<T>,
) => filter;
